import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Award, ChevronRight } from 'lucide-react';
import { ApiError } from '../api/client';
import {
  getProduktySoutezDetail,
  listProduktySouteze,
  type ProduktySoutezDetail,
  type ProduktySoutezItem,
  type ProduktySoutezKind,
} from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Button } from '../components/arc/button/button';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { Card } from '../components/arc/card/card';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './users-seznam.css';
import './produkty-souteze.css';

type SubTab = ProduktySoutezKind;

const intFmt = (n: number) => n.toLocaleString('cs-CZ');
const czkFmt = (n: number) => `${n.toLocaleString('cs-CZ')} Kč`;

function dateTimeFmt(s: string | null): string {
  if (!s) return '-';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  const [, y, mo, d, h, mi] = m;
  return `${Number(d)}. ${Number(mo)}. ${y} ${h}:${mi}`;
}

function statusBadge(status: string | null): ReactNode {
  if (!status) return <span className="text-muted">-</span>;
  return <>{status}</>;
}

function statusTone(status: string | null): string {
  const value = (status ?? '').toLocaleLowerCase('cs-CZ');
  if (value.includes('běží')) return 'running';
  if (value.includes('plán')) return 'scheduled';
  return 'closed';
}

function levelBadge(level: string | null): ReactNode {
  if (!level) return <span className="text-muted">-</span>;
  return <>{level}</>;
}

function countryCell(country: string | null): ReactNode {
  if (!country) return <span className="text-muted">-</span>;
  const parts = country.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return <span className="text-muted">-</span>;
  return <>{parts.join(', ')}</>;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Všechny' },
  { value: 'běží', label: 'Běží' },
  { value: 'naplánováno', label: 'Naplánováno' },
  { value: 'vyčerpáno', label: 'Vyčerpáno' },
  { value: 'vypnuto', label: 'Vypnuto' },
  { value: 'ukončeno', label: 'Ukončeno' },
];

const LIMIT_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: 'all', label: 'vše' },
];
const ALL_LIMIT = 100000;

export function ProduktySouvezePage() {
  const [selectedGameId, setSelectedGameId] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get('gameId');
    const n = Number(raw);
    return raw != null && raw !== '' && Number.isFinite(n) ? n : null;
  });

  useEffect(() => {
    const onPop = () => {
      const raw = new URLSearchParams(window.location.search).get('gameId');
      const n = Number(raw);
      setSelectedGameId(raw != null && raw !== '' && Number.isFinite(n) ? n : null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function openDetail(gameId: number) {
    const sp = new URLSearchParams(window.location.search);
    sp.set('gameId', String(gameId));
    window.history.pushState(null, '', `${window.location.pathname}?${sp.toString()}`);
    setSelectedGameId(gameId);
  }

  function backToList() {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('gameId');
    const qs = sp.toString();
    window.history.pushState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
    setSelectedGameId(null);
  }

  if (selectedGameId != null) {
    return <SoutezDetail gameId={selectedGameId} onBack={backToList} />;
  }
  return <SoutezList onOpen={openDetail} />;
}

const SOUTEZ_TABS: readonly SubTab[] = ['running', 'scheduled'];

function SoutezList({ onOpen }: { onOpen: (gameId: number) => void }) {
  const [tab, setTab] = useTabParam<SubTab>('tab', SOUTEZ_TABS, 'running');
  const [items, setItems] = useState<ProduktySoutezItem[]>([]);
  const [total, setTotal] = useState(0);
  const [sumValue, setSumValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [counts, setCounts] = useState<{ running: number | null; scheduled: number | null }>({
    running: null,
    scheduled: null,
  });

  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('all');
  const [limit, setLimit] = useState('50');
  const [query, setQuery] = useState('');
  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listProduktySouteze({ kind: 'running', limit: 1 }),
      listProduktySouteze({ kind: 'scheduled', limit: 1 }),
    ])
      .then(([r, s]) => {
        if (cancelled) return;
        setCounts({ running: r.total, scheduled: s.total });
      })
      .catch(() => {
        /* badge počty nejsou kritické */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listProduktySouteze({
      kind: tab,
      status: status !== 'all' ? status : undefined,
      month: month !== 'all' ? month : undefined,
      search: query.trim() || undefined,
      limit: limit === 'all' ? ALL_LIMIT : Number(limit),
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setSumValue(res.sumValue);
        setMonths(res.months);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'Načtení selhalo');
        setItems([]);
        setTotal(0);
        setSumValue(0);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, status, month, limit, query]);

  useFeather([items, tab]);

  function reset() {
    setStatus('all');
    setMonth('all');
    setLimit('50');
    setQuery('');
  }

  const monthOptions = useMemo(
    () => [{ value: 'all', label: 'Všechny' }, ...months.map((m) => ({ value: m, label: m }))],
    [months],
  );

  const tabCount = (k: SubTab) => (counts[k] == null ? '…' : intFmt(counts[k] as number));
  const tableRows = items.map((item) => ({ ...item, detail: item.gameId ?? -1 }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'name', label: 'Název', width: 230, render: (_, row) => row.gameId != null ? <button type="button" className="rondo-contest-link" onClick={() => onOpen(row.gameId as number)}><span className="rondo-contest-avatar"><Award size={16} aria-hidden="true" /></span><span><strong>{row.name ?? '—'}</strong><small>Soutěž #{row.gameId}</small></span></button> : <span>{row.name ?? '—'}</span> },
    { key: 'country', label: 'Země', width: 110, render: (_, row) => countryCell(row.country) },
    { key: 'level', label: 'Level', width: 90, render: (_, row) => levelBadge(row.level) },
    { key: 'startDt', label: 'Začátek', width: 155, render: (_, row) => dateTimeFmt(row.startDt) },
    { key: 'endDt', label: 'Expirace', width: 155, render: (_, row) => dateTimeFmt(row.endDt) },
    { key: 'valueCzk', label: 'Hodnota', width: 115, numeric: true, render: (_, row) => row.valueCzk == null ? '—' : czkFmt(row.valueCzk) },
    { key: 'spins', label: 'Spinů', width: 90, numeric: true, render: (_, row) => row.spins == null ? '—' : intFmt(row.spins) },
    { key: 'status', label: 'Status', width: 105, render: (_, row) => <span className="rondo-contest-status" data-tone={statusTone(row.status)}>{statusBadge(row.status)}</span> },
    { key: 'detail', label: '', width: 48, sortable: false, render: (_, row) => row.gameId == null ? null : <button type="button" className="rondo-arc-user-detail" aria-label={`Detail soutěže ${row.name ?? row.gameId}`} onClick={() => onOpen(row.gameId as number)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];

  return (
    <section className="rondo-arc-users-list rondo-arc-contests p1-arc-scope"><header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">PRODUKTY</div><h1>Soutěže</h1><p>Běžící a plánované soutěže Ronda</p></div></header>
      <div className="rondo-arc-users-list-metrics">
        <MetricCard label="Běžící" value={counts.running ?? 0} context={counts.running == null ? 'Načítám…' : 'Aktuální soutěže'} />
        <MetricCard label="Naplánované" value={counts.scheduled ?? 0} context={counts.scheduled == null ? 'Načítám…' : 'Připravené soutěže'} />
        <MetricCard label="V aktuálním výpisu" value={total} context={tab === 'running' ? 'Běžících soutěží' : 'Naplánovaných soutěží'} />
        <MetricCard label="Hodnota výher" value={sumValue} suffix={'\u00a0Kč'} context="Součet aktuálního výpisu" />
      </div>

      <ArcTabs label="Stav soutěží" items={[{ value: 'running', label: `Běžící ${tabCount('running')}` }, { value: 'scheduled', label: `Naplánované ${tabCount('scheduled')}` }]} value={tab} onChange={setTab} className="rondo-contest-tabs" />

      <ServiceBar
        variant="seg"
        selects={[
          { label: 'Status', value: status, onChange: setStatus, options: STATUS_OPTIONS },
          { label: 'Měsíc', value: month, onChange: setMonth, options: monthOptions },
          { label: 'Limit', value: limit, onChange: setLimit, options: LIMIT_OPTIONS },
        ]}
        onReset={reset}
        search={query}
        onSearch={setQuery}
        searchPlaceholder="název / level…"
      />

      <div className="rondo-arc-users-list-table rondo-arc-contests-table">
        {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
        {loading ? <p className="rondo-arc-users-loading">Načítám soutěže…</p> : <SortableDataTable rows={tableRows} columns={columns} rowKey="cacheKey" caption="Soutěže Ronda" emptyMessage="Žádná soutěž neodpovídá filtru." />}
      </div>

      <p className="rondo-contest-count">{intFmt(items.length)} zobrazených soutěží{total > items.length ? ` z ${intFmt(total)}` : ''}</p>
    </section>
  );
}

function SoutezDetail({ gameId, onBack }: { gameId: number; onBack: () => void }) {
  const [data, setData] = useState<ProduktySoutezDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProduktySoutezDetail(gameId)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  useFeather([data]);

  if (loading) {
    return (
      <section className="rondo-arc-contest-detail p1-arc-scope"><Button variant="secondary" size="sm" onClick={onBack}>Zpět na soutěže</Button><PageState title="Načítám soutěž" loading /></section>
    );
  }
  if (error || !data) {
    return (
      <section className="rondo-arc-contest-detail p1-arc-scope"><Button variant="secondary" size="sm" onClick={onBack}>Zpět na soutěže</Button><PageState title="Soutěž se nepodařilo načíst" message={error ?? 'Soutěž nenalezena.'} /></section>
    );
  }

  const { meta, sample, topPlayers } = data;
  const title = meta.name ?? (meta.valueCzk != null ? czkFmt(meta.valueCzk) : `Soutěž #${meta.gameId}`);
  const playerRows = topPlayers.map((player, index) => ({ ...player, rank: index + 1 }));
  type PlayerRow = (typeof playerRows)[number];
  const playerColumns: DataColumn<PlayerRow>[] = [
    { key: 'rank', label: '#', width: 55, numeric: true },
    { key: 'userId', label: 'Uživatel', width: 240, numeric: false, render: (_, row) => <span className="rondo-contest-player"><strong>{row.name ?? `Hráč ${row.userId}`}</strong><small>{[row.email, `ID ${row.userId}`].filter(Boolean).join(' · ')}</small></span> },
    { key: 'spinCount', label: 'Počet spinů', numeric: true, width: 145, render: (_, row) => intFmt(row.spinCount) },
    { key: 'winCount', label: 'Z toho výhry', numeric: true, width: 140, render: (_, row) => intFmt(row.winCount) },
    { key: 'firstSpin', label: 'První spin', width: 150, render: (_, row) => dateTimeFmt(row.firstSpin) },
    { key: 'lastSpin', label: 'Poslední spin', width: 150, render: (_, row) => dateTimeFmt(row.lastSpin) },
  ];

  return (
    <section className="rondo-arc-contest-detail p1-arc-scope">
      <Button variant="secondary" size="sm" onClick={onBack}>Zpět na soutěže</Button>
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">SOUTĚŽ</div><h1>{title}</h1><p>{[meta.country, meta.level, meta.status].filter(Boolean).join(' · ')}</p></div></header>
      <div className="rondo-arc-users-list-metrics rondo-contest-detail-metrics">
        <MetricCard label="Hodnota výhry" value={meta.valueCzk ?? 0} suffix={'\u00a0Kč'} context={meta.valueCzk == null ? 'Hodnota není dostupná' : 'Hodnota soutěže'} />
        <MetricCard label="Unikátních hráčů" value={sample.uniquePlayers} context="Ve vzorku" />
        <MetricCard label="Celkem spinů" value={sample.totalSpins} context="Ve vzorku" />
        <MetricCard label="Výherních spinů" value={sample.winSpins} context="Spiny s výhrou" />
      </div>
      <Card title="Údaje o soutěži" description={`Game ID ${meta.gameId}`} className="rondo-contest-meta-card"><dl><div><dt>Začátek</dt><dd>{dateTimeFmt(meta.startDt)}</dd></div><div><dt>Konec</dt><dd>{dateTimeFmt(meta.endDt)}</dd></div><div><dt>Spinů na hráče</dt><dd>{intFmt(sample.avgSpinsPerPlayer)}</dd></div></dl></Card>
      <Card title="Nejaktivnější hráči" description="Vzorek spinů z aktuálních dat" className="rondo-contest-players-card"><SortableDataTable rows={playerRows} columns={playerColumns} rowKey="userId" caption="Nejaktivnější hráči soutěže" emptyMessage="Žádný vzorek spinů není dostupný." /></Card>
    </section>
  );
}
